import { useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import { syncCatalogSnapshotFromRemote } from '../../catalog/infra-api';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { captureCatch } from '../../shared/sentry/sentryUser';
import { homeDbg } from '../../shared/dev/homeDebug';
import { POS_LIVE_CATALOG_PROBE_INTERVAL_MS, shouldRunPosLiveCatalogProbe } from './posLiveCatalogProbe';

function readAppVisible(): boolean {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    return document.visibilityState === 'visible';
  }
  return AppState.currentState === 'active';
}

/**
 * Baja catálogo/stock incremental mientras el POS está en foco.
 * force sin forceFull: saltea cooldown de 15 min, sigue siendo p_updated_since.
 */
export function usePosLiveCatalogProbe(isFocused: boolean): void {
  const isFocusedRef = useRef(isFocused);
  isFocusedRef.current = isFocused;

  useEffect(() => {
    if (!isFocused) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let visible = readAppVisible();
    let inFlight = false;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = () => {
      if (cancelled) return;
      clearTimer();
      timer = setTimeout(() => {
        void tick();
      }, POS_LIVE_CATALOG_PROBE_INTERVAL_MS);
    };

    const tick = async () => {
      if (cancelled || inFlight) return;
      const online = await isOnline().catch(() => false);
      if (
        !shouldRunPosLiveCatalogProbe({
          isFocused: isFocusedRef.current,
          isVisible: visible,
          isOnline: online,
        })
      ) {
        if (visible && isFocusedRef.current) schedule();
        return;
      }
      inFlight = true;
      homeDbg('POS.probe', 'incremental snapshot');
      try {
        await syncCatalogSnapshotFromRemote({ force: true });
      } catch (error) {
        captureCatch('pos', 'live-catalog-probe')(error);
      } finally {
        inFlight = false;
        if (visible && isFocusedRef.current && !cancelled) schedule();
      }
    };

    void tick();

    const appSub = AppState.addEventListener('change', (state) => {
      visible = state === 'active';
      if (visible && isFocusedRef.current) {
        clearTimer();
        void tick();
      } else {
        clearTimer();
      }
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        visible = document.visibilityState === 'visible';
        if (visible && isFocusedRef.current) {
          clearTimer();
          void tick();
        } else {
          clearTimer();
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      clearTimer();
      appSub.remove();
      removeVisibility?.();
    };
  }, [isFocused]);
}
